import { getAssetPath } from "@stencil/core";
import ky from "ky";
import { Observable, BehaviorSubject } from "rxjs";
import { DateTimeFactory } from "./dateTime.factory";
import { AVBusService } from "./bus.service";
import { HandledError } from "../models/HandledError";
import { ErrorCode } from "../models/enums";

export interface ITranslationMap {
  [key: string]: string;
}

class TranslateServiceCtr {
  private languages: Map<string, ITranslationMap> = new Map();
  private userLanguages: Map<string, ITranslationMap> = new Map();
  private activeLanguage: string;

  private langSubject: BehaviorSubject<{ [key: string]: string }> =
    new BehaviorSubject({});
  public langChange$ = this.langSubject.asObservable();

  private supportedLanguages = [
    "ar",
    "de",
    "el",
    "en",
    "es",
    "fi",
    "fr",
    "nl",
    "pl",
    "sv",
    "zh",
    "pt",
    "it",
  ];

  constructor() {
    // pre-fill supported languages slots
    this.supportedLanguages.forEach((lang) => this.languages.set(lang, {}));
  }

  /**
   *  sets what language to use
   * @param lang iso language
   */
  public async use(lang) {
    try {
      this.activeLanguage = lang;

      // either our translations or a registered one
      if (this.languages.has(lang)) {
        // get our translations
        if (this.supportedLanguages.includes(lang)) {
          const path = getAssetPath(`auvious-i18n/${lang}.json`);
          const langMap: ITranslationMap = await ky.get(path).json();

          // merge with existing map
          const existingMap = this.languages.get(lang);
          this.languages.set(lang, { ...langMap, ...existingMap });
        }
      } else {
        this.languages.set(lang, {});
      }
      this.notify();
      // localize dates
      DateTimeFactory.locale(lang);
    } catch (ex) {
      if (lang !== "en") this.use("en");
      AVBusService.errorLog(
        new HandledError(ex, ErrorCode.LOCALE_TRANSLATION_LOAD_FAILED)
      );
    }
  }

  /**
   * Gets a translation instantly. Use while using the app, to ensure translations are loaded
   * @param key key that corresponds to a translation
   */
  public instant(key): string {
    return this.translations?.[key] ?? key;
  }

  /**
   * Subscribes to a translation and translation updates accordingly
   */
  public getTranslations(): Observable<{ [key: string]: string }> {
    return this.langSubject;
  }

  /**
   *
   * @param map translations map key:value pairs. Merges and overwrites existing ones if common
   */
  public registerTranslation(language: string, map: ITranslationMap) {
    const existingMap = this.languages.get(language);
    this.userLanguages.set(language, map);
    this.languages.set(language, { ...existingMap, ...map });
    this.notify();
  }

  private notify() {
    this.langSubject.next(this.translations);
  }

  private get translations() {
    return this.languages.get(this.activeLanguage);
  }
}

export const TranslateService = new TranslateServiceCtr();
